import type { ReactNode } from "react";
import { Link } from "react-router-dom";

// The shared DETAIL-page header (Session AQW, Phase 2 record pages). PageHeader (Session AQT) was
// deliberately scoped to list pages only -- every detail page in the app (Contract, Customer, Lift,
// Site, Project and the rest) still hand-rolls its own `<h1 className="font-heading text-3xl...">`,
// each with its own slightly different back-link/status/actions arrangement or none at all. This is
// that shape, built once Phase 2 actually needed it rather than guessed at in Phase 1.
//
// Deliberately NOT a PageHeader variant: a detail page always has somewhere it came FROM (its list),
// and often has a STATUS the page is primarily about (a quote's status, a call-out's status) sitting
// beside the title rather than buried in a fact row below it -- neither exists on a list page.

export interface RecordHeaderProps {
  title: ReactNode;
  backTo: string;
  backLabel?: string;
  status?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function RecordHeader({
  title,
  backTo,
  backLabel = "Back",
  status,
  subtitle,
  actions,
  className = "",
}: RecordHeaderProps) {
  return (
    <div className={className}>
      <Link
        to={backTo}
        className="mb-2 inline-flex items-center gap-1 text-xs text-ele-body opacity-70 transition hover:text-ele-purple hover:opacity-100"
      >
        <span aria-hidden>&larr;</span> {backLabel}
      </Link>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {/* The title block gives way first (Session BDU; see PageHeader): the actions keep their natural
            width on a desktop and shrink only to their widest control. */}
        <div className="min-w-0 sm:min-w-[12rem] sm:[flex-shrink:999]">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-3xl tracking-wide text-ele-strong">{title}</h1>
            {status}
          </div>
          {subtitle != null && <p className="mt-1 text-sm text-ele-body">{subtitle}</p>}
        </div>
        {actions != null && (
          <div className="flex min-w-min max-w-full flex-wrap items-center gap-2 [&>a]:whitespace-nowrap [&>button]:whitespace-nowrap">{actions}</div>
        )}
      </div>
    </div>
  );
}
